import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ObjectToolbar } from 'src/blocks/object-toolbar';
import { useObjectToolbarFormStore } from 'src/blocks/object-toolbar/useObjectToolbarFormStore';
import CustomPdfExportModal from 'src/components/custom-pdf-export-modal/CustomPdfExportModal';

import type { FilterModal } from '@/components/customisable-ribbon/customisableRibbonModalSchema';
import { useExportOptions } from '@/hooks/useExportOptions';
import type { TablePropsWithActions, TableRecord } from '@/utils/table/types';

import { buildControlRegisterToolbarConfig } from './registerToolbarConfig';

interface Props<T extends TableRecord> {
  title: string;
  /** Raw (unparenthesised) count — the toolbar wraps it itself. */
  counter?: string;
  tableProps: TablePropsWithActions<T>;
  getActiveRibbonFilters: () => FilterModal[];
}

/**
 * Object toolbar for the Controls registers (main Controls register + Control
 * Tests register), mounted only when the `object_toolbar` flag is on. Generic
 * over the table-fields type so both registers share one component (CUS-153).
 *
 * Owning `useExportOptions` here (rather than on the page) keeps it off the
 * legacy `ExportButton` path — with the flag off this component never renders, so
 * the export options are computed once, by whichever path is actually shown.
 * Mirrors the Indicator pilot's `IndicatorRegisterToolbar` (CUS-150).
 */
export const ControlRegisterToolbar = <T extends TableRecord>({
  title,
  counter,
  tableProps,
  getActiveRibbonFilters,
}: Props<T>) => {
  const { t } = useTranslation(['common']);
  const [showPdfModal, setShowPdfModal] = useState(false);
  // Ribbon "Edit Filters" opener bridged up from CustomisableRibbon (relocate
  // mode). Present only when the user can manage the ribbon; drives the toolbar's
  // overflow "Edit" group.
  const ribbonConfig = useObjectToolbarFormStore((s) => s.ribbonConfigState);
  const onShowCustomPdfModal = useCallback(() => setShowPdfModal(true), []);

  const { exportOptions, exportToPdf } = useExportOptions<T>({
    tableProps,
    entityLabel: title,
    getActiveRibbonFilters,
    onShowCustomPdfModal,
  });

  const toolbarConfig = useMemo(
    () =>
      buildControlRegisterToolbarConfig({
        title,
        counter,
        exportOptions,
        editRibbon: ribbonConfig
          ? {
              groupLabel: t('edit'),
              label: t('customisableRibbons.editRibbon'),
              onClick: ribbonConfig.openEditRibbon,
            }
          : undefined,
      }),
    [title, counter, exportOptions, ribbonConfig, t]
  );

  return (
    <>
      <ObjectToolbar config={toolbarConfig} />
      <CustomPdfExportModal
        exportToPdf={async (options) => {
          await exportToPdf({
            titleOverride: options.title,
            subtitleOverride: options.subtitle,
            hideRibbon: options.hideRibbon,
            orientation: options.orientation,
          });
        }}
        entityLabel={title}
        showModal={showPdfModal}
        setShowModal={setShowPdfModal}
        showRibbonOptions={true}
      />
    </>
  );
};
